import React, { useEffect, useState, useRef } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, IconButton, Box, TextField, Popover, Typography,
  Autocomplete, Tooltip, Chip, Menu, MenuItem, Grid, Divider
} from "@mui/material";
import {
  Print, Visibility, Description as FileIcon,
  ArrowDropDown as ArrowDropDownIcon
} from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";
import { Collapse } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getJournalVouchersbydivision,
  DivisionList,
  ApproveGeneralVoucher,
  JournalVoucherExport,
} from "../../../Services/Services";
import { generatePdf } from "../../components/Accounts/GeneralVoucherPrint";
import InfiniteScroll from "react-infinite-scroll-component";
import { CircularProgress } from "@mui/material";

const GeneralVoucher = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedRow, setExpandedRow] = useState(null);
  // ========== State Definitions ==========
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [generalVouchers, setGeneralVouchers] = useState([]);
  const [divisionLists, setDivisionLists] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);

  // Approval states
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [GeneralToApprove, setGeneralToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [showRemarkForGeneralId, setShowRemarkForGeneralId] = useState(null);
  const [actionType, setActionType] = useState(null);

  // ✅ NEW: Date Range States
  const [dateRange, setDateRange] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  // ⭐ ADD THESE TWO NEW STATES
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({ from: "", to: "" });

  // Filter states
  const [selectedDivision, setSelectedDivision] = useState(null);

  // ========== Helper: Format Date for Display ==========
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // ========== Helper: Get Date Range Display Text ==========
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);

      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);

      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;

      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;

      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;

      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;

      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;

      default:
        return "";
    }
  };

  // ========== Fetch Division List ==========
  useEffect(() => {
    const fetchData = async () => {
      const divisions = await DivisionList();
      setDivisionLists(divisions || []);
    };
    fetchData();
  }, []);

  // ========== Fetch Vouchers Function ==========
  const fetchGeneralVouchers = async (pageNum = 1, reset = false) => {
    try {
      setLoading(true);

      const params = {};

      // Division filter
      if (selectedDivision) {
        params.division_id = selectedDivision.division_id;
      }

      // ✅ Date Range filter
      params.date_range = dateRange;

      if (dateRange === "custom" && customDateRange.from && customDateRange.to) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      console.log("🔍 API Call with params:", params);

      const data = await getJournalVouchersbydivision(pageNum, params);
      console.log("✅ API Response:", data);

      const results = data?.results || [];
      const next = data?.next ?? null;

      if (reset || pageNum === 1) {
        setGeneralVouchers(results);
      } else {
        setGeneralVouchers((prev) => [...prev, ...results]);
      }

      setHasMore(Boolean(next));
      setPage(pageNum);
    } catch (error) {
      console.error("❌ Failed to fetch vouchers:", error);
      toast.error("Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Auto-fetch on filter change
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchGeneralVouchers(1, true);
    }, 500);

    return () => clearTimeout(handler);
  }, [selectedDivision, dateRange, customDateRange]);

 const handleExcelExport = async () => {
  if (generalVouchers.length === 0) {
    toast.warning("No data available to export");
    return;
  }

  setExcelLoading(true);
  try {
    const filters = {
      date_range: dateRange,
    };
    
    if (selectedDivision) {
      filters.division_id = selectedDivision.division_id;
    }

    const blob = await JournalVoucherExport(filters, customDateRange);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Journal_Vouchers_${new Date().getTime()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success("Excel downloaded successfully!");
  } catch (error) {
    console.error("Export error:", error);
    toast.error("Failed to export Excel");
  } finally {
    setExcelLoading(false);
  }
};

  const fetchMoreData = () => {
    const nextPage = page + 1;
    fetchGeneralVouchers(nextPage);
  };

  // ========== Reset Filters ==========
  const handleResetFilters = () => {
    setSelectedDivision(null);
    setDateRange("today");
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" }); // ⭐ ADD THIS LINE
    setShowCustomDateInputs(false); // ⭐ ADD THIS LINE
    setGeneralVouchers([]);
    setHasMore(true);
    setPage(1);
  };

  // ========== Handle Approve/Reject ==========
  const handleApprove = async (voucher) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveGeneralVoucher(
        voucher.gen_voucher_no,
        approvedById,
        voucher.status,
        remarkInput
      );

      setGeneralVouchers((prev) =>
        prev.map((item) =>
          item.gen_voucher_no === voucher.gen_voucher_no ? updated : item
        )
      );

      toast.success(
        updated.status === "Approved"
          ? "General voucher approved successfully!"
          : updated.status === "Rejected"
            ? "General voucher rejected!"
            : "General voucher updated!"
      );

      setApproveAnchorEl(null);
      setGeneralToApprove(null);
      setRemarkInput("");
      setShowRemarkForGeneralId(null);
      setActionType(null);
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error("Failed to approve General Voucher.");
    }
  };

  // ========== Helper Functions ==========
  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // ========== Prevent body scroll ==========
  useEffect(() => {
    document.body.style.overflow = isMobile ? 'auto' : 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile]);


  // ⭐ ADD THESE TWO FUNCTIONS AFTER formatDateDisplay

  // ✅ Apply Custom Date Range
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({ ...tempCustomDateRange });
      setDateRange("custom");
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };


  return (
    <>
      <ToastContainer />

      {/* ========== FILTERS SECTION ========== */}
      <Box sx={{ mt: -4, pt: 0, mb: 2, width: "100%" }}>
        <Grid container spacing={2} alignItems="center">
          {/* ✅ Date Range Dropdown */}
          <Grid item xs={12} sm={5}>
            <TextField
              label="Date Range"
              value={
                dateRange === "custom" && customDateRange.from && customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : dateRange === "today"
                    ? "Today"
                    : dateRange === "yesterday"
                      ? "Yesterday"
                      : dateRange === "this_week"
                        ? "This Week"
                        : dateRange === "previous_week"
                          ? "Previous Week"
                          : dateRange === "this_month"
                            ? "This Month"
                            : dateRange === "current_year"
                              ? "Current Year"
                              : dateRange === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
              fullWidth
            />
          </Grid>

          {/* ⭐ REPLACE OLD MENU WITH THIS */}
          <Menu
            anchorEl={dateMenuAnchor}
            open={Boolean(dateMenuAnchor)}
            onClose={() => {
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
              setTempCustomDateRange({ from: "", to: "" });
            }}
            PaperProps={{
              sx: { width: 350, maxHeight: 500 },
            }}
          >
            {/* Quick Ranges */}
            {[
              { key: "today", label: "Today" },
              { key: "yesterday", label: "Yesterday" },
              { key: "this_week", label: "This Week" },
              { key: "previous_week", label: "Previous Week" },
              { key: "this_month", label: "This Month" },
              { key: "current_year", label: "Current Year" },
              { key: "financial_year", label: "Financial Year" },
            ].map((item) => (
              <MenuItem
                key={item.key}
                onClick={() => {
                  setDateRange(item.key);
                  setCustomDateRange({ from: "", to: "" });
                  setDateMenuAnchor(null);
                  setShowCustomDateInputs(false);
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2">{item.label}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {getDateRangeDisplay(item.key)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}

            <Divider />

            {/* ✅ Custom Date Range - Expandable */}
            <MenuItem
              onClick={() => setShowCustomDateInputs((prev) => !prev)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
              >
                Custom Date Range
              </Typography>
              {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
            </MenuItem>

            <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  bgcolor: "#f5f5f5",
                  borderTop: "1px solid #e0e0e0",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Select Date Range
                </Typography>

                {/* From and To Date Inputs Side by Side */}
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    value={tempCustomDateRange.from}
                    onChange={(e) =>
                      setTempCustomDateRange((prev) => ({
                        ...prev,
                        from: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: tempCustomDateRange.to || undefined,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        height: 40,
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />

                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    value={tempCustomDateRange.to}
                    onChange={(e) =>
                      setTempCustomDateRange((prev) => ({
                        ...prev,
                        to: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: tempCustomDateRange.from || undefined,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        height: 40,
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                </Box>

                {/* Selected Range Preview */}
                {tempCustomDateRange.from && tempCustomDateRange.to && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 1.5,
                      p: 1,
                      bgcolor: "#e8f5e9",
                      borderRadius: 1,
                      textAlign: "center",
                      color: "#2e7d32",
                      fontWeight: 500,
                    }}
                  >
                    📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                    {formatDateDisplay(tempCustomDateRange.to)}
                  </Typography>
                )}

                {/* Apply and Cancel Buttons */}
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelCustomDateRange}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyCustomDateRange}
                    disabled={
                      !tempCustomDateRange.from || !tempCustomDateRange.to
                    }
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Menu>

          {/* Division Filter */}
          {/* <Grid item xs={12} sm={5}>
            <Autocomplete
              options={divisionLists || []}
              value={selectedDivision}
              onChange={(e, newVal) => setSelectedDivision(newVal)}
              getOptionLabel={(opt) => opt?.division || ""}
              isOptionEqualToValue={(opt, val) =>
                opt?.division_id === val?.division_id
              }
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Division"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 35,
                      borderRadius: 0,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.7rem",
                    },
                  }}
                />
              )}
            />
          </Grid> */}

          {/* Reset Button */}
          <Grid item xs={12} sm={1.5}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleResetFilters}
              sx={{
                height: "35px",
                whiteSpace: "nowrap",
                borderRadius: "0",
                fontSize: "0.7rem",
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>

          {/* Excel Export Button */}
          <Grid item xs={12} sm={0.5}>
            <Box display="flex" justifyContent={{ xs: "flex-end", sm: "center" }}>
              <IconButton
                color="success"
                size="small"
                onClick={handleExcelExport}
                disabled={excelLoading || generalVouchers.length === 0}
                sx={{
                  borderRadius: 0,
                  padding: "6px",
                }}
              >
                {excelLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ✅ CONDITIONAL: Mobile Cards | Desktop Table */}
      {isMobile ? (
        /* 📱 MOBILE VIEW - Expandable Cards */
        <Box >
          <InfiniteScroll
            dataLength={generalVouchers.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <Typography align="center" sx={{ py: 2, fontSize: "0.75rem" }}>
                Loading...
              </Typography>
            }
          >
            {generalVouchers.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                  No vouchers found
                </Typography>
              </Box>
            ) : (
              <>
                {generalVouchers.map((voucher, index) => (
                  <Box key={index}>
                    {/* Main Card - Always Visible */}
                    <Box
                      onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        cursor: "pointer",
                        bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:active": {
                          bgcolor: "#e3f2fd",
                        },
                        borderLeft: `4px solid ${voucher.status === "Approved"
                          ? "#4caf50"
                          : voucher.status === "Rejected"
                            ? "#f44336"
                            : "#ff9800"
                          }`,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        {/* Voucher No & Status */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                            {voucher.gen_number}
                          </Typography>
                          <Chip
                            label={
                              voucher.status === "Approved"
                                ? "Approved"
                                : voucher.status === "Rejected"
                                  ? "Rejected"
                                  : "Pending"
                            }
                            color={
                              voucher.status === "Approved"
                                ? "success"
                                : voucher.status === "Rejected"
                                  ? "error"
                                  : "warning"
                            }
                            size="small"
                            sx={{ fontSize: "0.6rem", height: 18 }}
                          />
                        </Box>

                        {/* Ledger Name */}
                        <Typography sx={{ fontSize: "0.7rem", color: "#666", mb: 0.3 }}>
                          {truncateText(
                            voucher.imprest_name ||
                            voucher.supplier_name ||
                            voucher.advanceworker_name ||
                            voucher.Transporter_name ||
                            "N/A",
                            25
                          )}
                        </Typography>

                        {/* Amount & Date */}
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: "bold",
                              color: "primary.main",
                            }}
                          >
                            ₹{Number(voucher.grandtotal || 0).toLocaleString("en-IN")}
                          </Typography>
                          <Typography sx={{ fontSize: "0.65rem", color: "#999" }}>
                            {formatDate(voucher.created_at)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Expand Icon */}
                      <IconButton size="small" sx={{ ml: 1 }}>
                        {expandedRow === index ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </Box>

                    {/* Expanded Details */}
                    <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                          borderLeft: `4px solid ${voucher.status === "Approved"
                            ? "#4caf50"
                            : voucher.status === "Rejected"
                              ? "#f44336"
                              : "#ff9800"
                            }`,
                        }}
                      >
                        {/* Bill Details */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 0.8 }}>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>BILL NO</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                              {voucher.bill_no || "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>BILL DATE</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                              {formatDate(voucher.bill_date)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Amount Details */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0.8, mb: 0.8 }}>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>TOTAL</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "primary.main" }}>
                              ₹{Number(voucher.grandtotal || 0).toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>PAID</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "success.main" }}>
                              ₹{Number(voucher.receipt_amount || 0).toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>BALANCE</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "error.main" }}>
                              ₹{Number(voucher.balance_amount || 0).toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Division & Project */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 0.8 }}>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>DIVISION</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                              {voucher.division_name || "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>PROJECT</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                              {voucher.project_name || "N/A"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Payment Status & Created By */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 0.8 }}>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>PAYMENT STATUS</Typography>
                            <Chip
                              label={voucher.payment_status || "Unknown"}
                              color={
                                voucher.payment_status === "Paid"
                                  ? "success"
                                  : voucher.payment_status === "Unpaid"
                                    ? "error"
                                    : "warning"
                              }
                              size="small"
                              sx={{ fontSize: "0.6rem", height: 18, mt: 0.3 }}
                            />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>CREATED BY</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                              {voucher.created_by_name || "N/A"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Narration */}
                        <Box sx={{ mb: 0.8 }}>
                          <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>NARRATION</Typography>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                            {voucher.narration || "N/A"}
                          </Typography>
                        </Box>

                        {/* Remarks */}
                        {voucher.remarks && (
                          <Box sx={{ mb: 0.8 }}>
                            <Typography sx={{ fontSize: "0.6rem", color: "#999" }}>REMARKS</Typography>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                              {voucher.remarks}
                            </Typography>
                          </Box>
                        )}

                        {/* Actions */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 0.5,
                            pt: 1,
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              generatePdf(voucher);
                            }}
                            sx={{
                              bgcolor: "#fff3e0",
                              borderRadius: "6px",
                              width: 32,
                              height: 32,
                              "&:hover": { bgcolor: "#ffe0b2" },
                            }}
                          >
                            <Print sx={{ fontSize: "1rem" }} />
                          </IconButton>

                          {typeof voucher.attachment === "string" && (
                            <a
                              href={voucher.attachment}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <IconButton
                                size="small"
                                color="info"
                                sx={{
                                  bgcolor: "#e3f2fd",
                                  borderRadius: "6px",
                                  width: 32,
                                  height: 32,
                                  "&:hover": { bgcolor: "#bbdefb" },
                                }}
                              >
                                <Visibility sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </a>
                          )}
                        </Box>
                      </Box>
                    </Collapse>

                    <Divider />
                  </Box>
                ))}

                {/* Total Summary Card */}
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "#e3f2fd",
                    borderTop: "2px solid #1976d2",
                  }}
                >
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: "bold", mb: 0.8 }}>
                    TOTALS
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#666" }}>TOTAL AMT</Typography>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "primary.main" }}>
                        ₹
                        {generalVouchers
                          .reduce((sum, v) => sum + parseFloat(v.grandtotal || 0), 0)
                          .toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#666" }}>PAID AMT</Typography>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "success.main" }}>
                        ₹
                        {generalVouchers
                          .reduce((sum, v) => sum + parseFloat(v.receipt_amount || 0), 0)
                          .toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#666" }}>BAL AMT</Typography>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "error.main" }}>
                        ₹
                        {generalVouchers
                          .reduce((sum, v) => sum + parseFloat(v.balance_amount || 0), 0)
                          .toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </InfiniteScroll>
        </Box>
      ) : (
        /* 💻 DESKTOP VIEW - Original Table */
        <InfiniteScroll
          dataLength={generalVouchers.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={
            <Typography align="center" sx={{ py: 2 }}>
              Loading...
            </Typography>
          }
          scrollableTarget="scrollableDiv"
        >
          <TableContainer
            id="scrollableDiv"
            component={Paper}
            sx={{ maxHeight: "88vh", overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1600,
                "& td, & th": {
                  fontWeight: "400",
                  borderBottom: "1px solid #ccc",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  backgroundColor: "#f5f5f5",
                  padding: "0px 16px",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold !important",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    S.No.
                  </TableCell>
                  <TableCell>Voucher No</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ladger Name</TableCell>
                  <TableCell>Bill No</TableCell>
                  <TableCell>Bill Date</TableCell>
                  <TableCell>Total Amt</TableCell>
                  <TableCell>Paid Amt</TableCell>
                  <TableCell>Bal Amt</TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Narration</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell
                    className="no-print"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      width: 200,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {generalVouchers.map((voucher, index) => (
                  <TableRow
                    hover
                    key={index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        "& td": {
                          backgroundColor: "#e3f2fd !important",
                        },
                      },
                      "& td": {
                        paddingTop: "2px !important",
                        paddingBottom: "2px !important",
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                        whiteSpace: "nowrap",
                        padding: "0px 16px",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        zIndex: 2,
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell>{voucher.gen_number}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          voucher.status === "Approved"
                            ? "Approved"
                            : voucher.status === "Rejected"
                              ? "Rejected"
                              : "Pending"
                        }
                        color={
                          voucher.status === "Approved"
                            ? "success"
                            : voucher.status === "Rejected"
                              ? "error"
                              : "warning"
                        }
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {voucher.imprest_name ||
                        voucher.supplier_name ||
                        voucher.advanceworker_name ||
                        voucher.Transporter_name}
                    </TableCell>
                    <TableCell>{voucher.bill_no}</TableCell>
                    <TableCell>{formatDate(voucher.bill_date)}</TableCell>
                    <TableCell>
                      {Number(voucher.grandtotal || 0).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell>
                      {Number(voucher.receipt_amount || 0).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell>
                      {Number(voucher.balance_amount || 0).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell>{voucher.division_name}</TableCell>
                    <TableCell>{voucher.project_name || ""}</TableCell>
                    <TableCell>{voucher.created_by_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          voucher.payment_status === "Paid"
                            ? "Paid"
                            : voucher.payment_status === "Unpaid"
                              ? "Unpaid"
                              : "Partial"
                        }
                        color={
                          voucher.payment_status === "Paid"
                            ? "success"
                            : voucher.payment_status === "Unpaid"
                              ? "error"
                              : "warning"
                        }
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{voucher.approved_name}</TableCell>
                    <TableCell>{formatDate(voucher.created_at)}</TableCell>
                    <TableCell>
                      <Tooltip title={voucher.narration} arrow>
                        <span>{truncateText(voucher.narration, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={voucher.remarks || ""} arrow>
                        <span>{truncateText(voucher.remarks || "", 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        zIndex: 2,
                      }}
                    >
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        color="warning"
                        size="small"
                        onClick={() => generatePdf(voucher)}
                      >
                        <Print fontSize="small" />
                      </IconButton>
                      <span
                        style={{
                          display: "inline-block",
                          width: 36,
                          textAlign: "center",
                        }}
                      >
                        {typeof voucher.attachment === "string" ? (
                          <a
                            href={`${voucher.attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              color="info"
                              sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                              size="small"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </a>
                        ) : voucher.attachment instanceof File ? (
                          <a
                            href={URL.createObjectURL(voucher.attachment)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              color="info"
                              sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                              size="small"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </a>
                        ) : (
                          <span style={{ display: "inline-block", width: 24 }} />
                        )}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Total Row */}
                <TableRow
                  sx={{
                    "& td": {
                      backgroundColor: "#E3F2FD !important",
                      color: "#000 !important",
                      fontWeight: "bold !important",
                      pt: 0.6,
                      pb: 0.6,
                    },
                  }}
                >
                  <TableCell
                    colSpan={6}
                    align="right"
                    sx={{ fontWeight: "bold !important" }}
                  >
                    Totals:
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold !important" }}>
                    {generalVouchers
                      .reduce((sum, v) => sum + parseFloat(v.grandtotal || 0), 0)
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold !important" }}>
                    {generalVouchers
                      .reduce(
                        (sum, v) => sum + parseFloat(v.receipt_amount || 0),
                        0
                      )
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold !important" }}>
                    {generalVouchers
                      .reduce(
                        (sum, v) => sum + parseFloat(v.balance_amount || 0),
                        0
                      )
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell>
                  <TableCell colSpan={9}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      )}

      {/* ========== Approve/Reject Popover ========== */}
      <Popover
        open={Boolean(approveAnchorEl)}
        anchorEl={approveAnchorEl}
        onClose={() => {
          setApproveAnchorEl(null);
          setGeneralToApprove(null);
          setRemarkInput("");
          setShowRemarkForGeneralId(null);
          setActionType(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          {showRemarkForGeneralId === GeneralToApprove?.gen_voucher_no ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Please enter remark before{" "}
                {actionType === "Approved" ? "approving" : "rejecting"}:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={remarkInput}
                onChange={(e) => setRemarkInput(e.target.value)}
                placeholder="Enter remark"
              />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Button
                  color={actionType === "Approved" ? "success" : "error"}
                  size="small"
                  onClick={() => {
                    if (!remarkInput.trim()) {
                      toast.error("Remark is required!");
                      return;
                    }
                    handleApprove({
                      ...GeneralToApprove,
                      status: actionType,
                      remarks: remarkInput,
                    });
                  }}
                >
                  Yes
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setApproveAnchorEl(null);
                    setGeneralToApprove(null);
                    setRemarkInput("");
                    setShowRemarkForGeneralId(null);
                    setActionType(null);
                  }}
                >
                  No
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Do you want to approve or reject this voucher?
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Button
                  color="success"
                  size="small"
                  onClick={() => {
                    setShowRemarkForGeneralId(GeneralToApprove?.gen_voucher_no);
                    setActionType("Approved");
                  }}
                >
                  Approve
                </Button>
                <Button
                  color="error"
                  size="small"
                  onClick={() => {
                    setShowRemarkForGeneralId(GeneralToApprove?.gen_voucher_no);
                    setActionType("Rejected");
                  }}
                >
                  Reject
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default GeneralVoucher;
